import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppButton, AppSingleSelect } from '@pro-sto/ui';
import type { SelectOption } from '@pro-sto/ui';
import { ExclamationMarkIcon, PlusIcon } from '@/components/icons';
import { ROUTES } from '@/constants/routes';
import { authStore, shiftDraftStore, shiftTemplatesStore, shiftsStore, toastStore } from '@/stores';
import { SHIFT_DATE_TAKEN } from '@/stores/ShiftDraftStore';
import { conflictOpensEditor } from '@/stores/shiftDraftModel';
import {
  findShiftForDate,
  toDateParam,
  weekBounds,
} from '../ShiftsWeek/shiftWeekModel';
import ShiftRoster from './ShiftRoster';
import './ShiftDay.css';

const NO_TEMPLATE_OPTION: SelectOption = { label: 'Без шаблона', value: null };

interface ShiftDayProps {
  date: Date;
}

const ShiftDay: React.FC<ShiftDayProps> = observer(({ date }) => {
  const navigate = useNavigate();
  const serviceCenterUuid = authStore.user?.service_center_uuid;
  const dateKey = toDateParam(date);
  const week = weekBounds(date);

  const [templateUuid, setTemplateUuid] = useState<string | null>(null);

  useEffect(() => {
    if (!serviceCenterUuid) return;
    void shiftTemplatesStore.loadTemplates(serviceCenterUuid);
  }, [serviceCenterUuid]);

  useEffect(() => {
    shiftDraftStore.clearCreateError();
    setTemplateUuid(null);
  }, [dateKey]);

  const inLoadedRange =
    shiftsStore.loadedFrom !== null &&
    shiftsStore.loadedTo !== null &&
    dateKey >= shiftsStore.loadedFrom &&
    dateKey <= shiftsStore.loadedTo;

  const shift = inLoadedRange ? findShiftForDate(shiftsStore.shifts, date) : undefined;

  const reloadWeek = () => {
    if (!serviceCenterUuid) return;
    shiftsStore.load(week.from, week.to, serviceCenterUuid);
  };

  const handleCreate = async () => {
    if (!serviceCenterUuid || shiftDraftStore.isCreating) return;
    const result = await shiftDraftStore.createShift({
      serviceCenterUuid,
      date: dateKey,
      templateUuid,
    });

    if (result.kind === 'created') {
      navigate(generatePath(ROUTES.SHIFT_EDIT, { uuid: result.uuid }), { replace: true });
      return;
    }

    if (result.kind !== 'conflict') return;

    await shiftsStore.load(week.from, week.to, serviceCenterUuid);
    if (result.existing && conflictOpensEditor(result.existing.status)) {
      toastStore.showError(SHIFT_DATE_TAKEN);
      navigate(generatePath(ROUTES.SHIFT_EDIT, { uuid: result.existing.uuid }), { replace: true });
      return;
    }
    if (result.existing?.status === 'closed') {
      toastStore.showError(SHIFT_DATE_TAKEN);
    }
  };

  if (!inLoadedRange) {
    if (shiftsStore.loadError && !shiftsStore.isLoading) {
      return (
        <div className="shift-day__error" data-testid="shifts-day-error">
          <ExclamationMarkIcon size={24} />
            <p className="shift-day__error-message" title={shiftsStore.loadError ?? undefined}>
              {shiftsStore.loadError}
            </p>
          <AppButton variant="secondary" onClick={reloadWeek} data-testid="shifts-day-retry">
            Повторить
          </AppButton>
        </div>
      );
    }

    return (
      <div className="shift-day__loading" data-testid="shifts-day-loading">
        <span className="shift-day__skeleton shift-day__skeleton_title" />
        <span className="shift-day__skeleton shift-day__skeleton_text" />
        <span className="shift-day__skeleton shift-day__skeleton_actions" />
      </div>
    );
  }

  if (shift) {
    return <ShiftRoster uuid={shift.uuid} />;
  }

  const templateOptions: SelectOption[] = [
    NO_TEMPLATE_OPTION,
    ...shiftTemplatesStore.templates.map((template) => ({
      label: template.name,
      value: template.uuid,
    })),
  ];
  const templateValue = templateUuid
    ? templateOptions.find((option) => option.value === templateUuid) ?? null
    : null;

  return (
    <div className="shift-day" data-testid="shifts-day-empty">
      <div className="shift-day__panel">
        <h2 className="shift-day__title">Смена не запланирована</h2>
        <p className="shift-day__text">
          Создайте смену, чтобы назначить сотрудников и указать время работы.
        </p>
        <div className="shift-day__actions">
          <div className="shift-day__template">
            <AppSingleSelect
              placeholder="Выбрать шаблон смены"
              options={templateOptions}
              value={templateValue}
              onChange={(option) => setTemplateUuid((option?.value as string | null) ?? null)}
              disabled={shiftTemplatesStore.templatesLoading || shiftDraftStore.isCreating}
              data-testid="shifts-day-template"
            />
          </div>
          <AppButton
            variant="primary"
            iconRight={<PlusIcon size={16} />}
            loading={shiftDraftStore.isCreating}
            onClick={handleCreate}
            data-testid="shifts-day-create"
          >
            Создать смену
          </AppButton>
        </div>
        {shiftTemplatesStore.templatesLoading ? (
          <p className="shift-day__text" data-testid="shifts-day-templates-loading">
            Загрузка шаблонов...
          </p>
        ) : null}
        {shiftTemplatesStore.templatesError ? (
          <div className="shift-day__inline-error" data-testid="shifts-day-templates-error">
            <p className="shift-day__error-message" title={shiftTemplatesStore.templatesError}>
              {shiftTemplatesStore.templatesError}
            </p>
            <AppButton
              variant="secondary"
              onClick={() =>
                serviceCenterUuid &&
                void shiftTemplatesStore.loadTemplates(serviceCenterUuid, { force: true })
              }
              disabled={shiftTemplatesStore.templatesLoading}
              data-testid="shifts-day-templates-retry"
            >
              Повторить
            </AppButton>
          </div>
        ) : null}
        {shiftDraftStore.createError ? (
          <p
            className="shift-day__error-message"
            title={shiftDraftStore.createError}
            data-testid="shifts-day-conflict"
          >
            {shiftDraftStore.createError}
          </p>
        ) : null}
      </div>
    </div>
  );
});

ShiftDay.displayName = 'ShiftDay';

export default ShiftDay;
